import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useChatbot } from '../../context/ChatbotContext';

const AdminChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      content: "Hello! I'm your admin assistant. How can I help you today?",
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef(null);
  const { currentUser } = useAuth();
  const { isAdminChatbotEnabled } = useChatbot();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setMessages((prev) => [...prev, { type: 'user', content: userMessage }]);

    // Simulate bot response
    const botResponse = getBotResponse(userMessage.toLowerCase());
    setTimeout(() => {
      setMessages((prev) => [...prev, { type: 'bot', content: botResponse }]);
    }, 500);
  };

  const getBotResponse = (message) => {
    // Handle job creation questions
    if (
      message.includes('add job') ||
      message.includes('post job') ||
      message.includes('create job') ||
      message.includes('new job') ||
      message.match(/how (do|can) i (add|post|create) (a )?new job/)
    ) {
      return (
        'To add a new job:\n' +
        '1. Go to the "Job Listings" page (/admin/job-listings).\n' +
        '2. Click the "Post Job" button or go directly to /post-job.\n' +
        '3. Fill out the job details and submit the form.\n' +
        'The new job will appear in the job listings.'
      );
    }

    // Handle application management questions
    if (
      message.includes('add application') ||
      message.includes('submit application') ||
      message.includes('review applications') ||
      message.includes('manage applications') ||
      message.match(/how (do|can) i (add|submit|review|manage) (an |the |)application(s)?/)
    ) {
      return (
        'To manage applications:\n' +
        '1. Go to the "Applications" page (/admin/applications).\n' +
        '2. Here you can view, review, and update the status of all applications.\n' +
        '3. Click on any application to see more details or take action.'
      );
    }

    // Handle employer contact management questions
    if (
      message.includes('employer contact') ||
      message.includes('add employer contact') ||
      message.includes('manage employer contacts')
    ) {
      return (
        'To manage employer contacts:\n' +
        '1. Go to the "Employer Contacts" page (/admin/employer-contacts).\n' +
        '2. Here you can view, add, or edit employer contacts.\n' +
        '3. Use the "Add Contact" button to add a new employer contact.'
      );
    }

    // Handle general contact management questions
    if (
      message.includes('general contact') ||
      message.includes('add general contact') ||
      message.includes('manage general contacts') ||
      message.includes('general inquiries')
    ) {
      return (
        'To manage general contacts/inquiries:\n' +
        '1. Go to the "General Inquiries" page (/admin/general-inquiries).\n' +
        '2. Here you can view and manage general contact requests.'
      );
    }

    // Handle ambiguous 'contacts' questions
    if (
      message.includes('contact') || message.includes('contacts')
    ) {
      return (
        'There are two types of contacts you can manage:\n' +
        '- Employer Contacts: For company/employer representatives (/admin/employer-contacts)\n' +
        '- General Contacts/Inquiries: For general messages and requests (/admin/general-inquiries)\n' +
        'Please specify which one you need help with!'
      );
    }

    // Handle bulletin management questions
    if (
      message.includes('add bulletin') ||
      message.includes('create bulletin') ||
      message.includes('manage bulletins') ||
      message.includes('bulletin management') ||
      message.match(/how (do|can) i (add|create|manage) (a |the |)bulletin(s)?/)
    ) {
      return (
        'To manage bulletins:\n' +
        '1. Go to the "Bulletins" page (/admin/bulletins).\n' +
        '2. Here you can create, edit, or delete bulletins.\n' +
        '3. Use the "Add Bulletin" button to create a new bulletin.'
      );
    }

    // Handle banner management questions
    if (
      message.includes('add banner') ||
      message.includes('create banner') ||
      message.includes('manage banners') ||
      message.includes('banner management') ||
      message.match(/how (do|can) i (add|create|manage) (a |the |)banner(s)?/)
    ) {
      return (
        'To manage banners:\n' +
        '1. Go to the "Banner Management" page (/admin/banners).\n' +
        '2. Here you can create, edit, or delete banners.\n' +
        '3. Use the "Add Banner" button to create a new banner.'
      );
    }

    // Handle notifications management questions
    if (
      message.includes('notification') ||
      message.includes('notifications') ||
      message.includes('send notification') ||
      message.includes('manage notifications') ||
      message.includes('notification management')
    ) {
      return (
        'To manage notifications:\n' +
        '1. Go to the "Bulletins" page (/admin/bulletins).\n' +
        '2. Notifications are typically sent via bulletins or banners.\n' +
        '3. Use the "Add Bulletin" or "Add Banner" options to notify users.'
      );
    }

    // Handle analytics questions
    if (
      message.includes('analytics') ||
      message.includes('view analytics') ||
      message.includes('analytics management') ||
      message.includes('view reports') ||
      message.includes('see statistics') ||
      message.includes('statistics')
    ) {
      return (
        'To view analytics:\n' +
        '1. Go to the "Analytics" page (/admin/analytics).\n' +
        '2. Here you can see job posting stats, application metrics, user engagement, and more.'
      );
    }

    // Handle onboarding documents questions
    if (
      message.includes('onboarding document') ||
      message.includes('onboarding documents') ||
      message.includes('upload onboarding') ||
      message.includes('manage onboarding documents') ||
      message.match(/how (do|can) i (add|upload|manage) (an |the |)onboarding document(s)?/)
    ) {
      return (
        'To manage onboarding documents:\n' +
        '1. Go to the "Onboarding Documents" page (/admin/onboarding).\n' +
        '2. Here you can upload, view, and manage onboarding documents for employees.'
      );
    }

    // Handle employee referrals questions
    if (
      message.includes('employee referral') ||
      message.includes('refer a candidate') ||
      message.includes('refer employee') ||
      message.includes('submit referral') ||
      message.match(/how (do|can) i (refer|submit) (an |the |)employee( referral|)/)
    ) {
      return (
        'To refer a candidate or manage employee referrals:\n' +
        '1. Go to the "Employee Referrals" page (/admin/employee-referrals) if available.\n' +
        '2. Follow the instructions to submit a referral or view referral status.\n' +
        '3. If you do not see this page, please contact your system administrator.'
      );
    }

    // Handle resignation questions
    if (
      message.includes('resignation') ||
      message.includes('submit resignation') ||
      message.includes('resignation process') ||
      message.includes('manage resignations')
    ) {
      return (
        'To manage resignations:\n' +
        '1. Go to the "Resignation" page (/admin/resignation).\n' +
        '2. Here you can view, process, or submit resignation requests.'
      );
    }

    // Handle training questions
    if (
      message.includes('training') ||
      message.includes('assign training') ||
      message.includes('training management') ||
      message.includes('employee training')
    ) {
      return (
        'To manage employee training:\n' +
        '1. Go to the "Training" page (/admin/training) if available.\n' +
        '2. Here you can assign, track, and review employee training modules.\n' +
        "3. If you don't see this page, please contact your system administrator."
      );
    }

    // Admin-specific responses
    const responses = {
      'help': 'I can help you with:\n- Navigating the admin panel\n- Managing jobs and applications\n- Handling employer contacts\n- Managing banners and bulletins\n- Viewing analytics\n- Managing employee documents\n\nWhat would you like to know more about?',
      
      'jobs': 'To manage jobs, you can:\n1. View all jobs at /admin/job-listings\n2. Create new jobs at /post-job\n3. Edit existing jobs by clicking on them\n4. View job analytics at /admin/analytics',
      
      'applications': 'To manage applications:\n1. View all applications at /admin/applications\n2. Click on any application to view details\n3. Update application status from the details page',
      
      'employer': 'For employer management:\n1. View employer contacts at /admin/employer-contacts\n2. View general inquiries at /admin/general-inquiries\n3. Manage employer-related settings in the settings page',
      
      'banners': 'To manage banners:\n1. Go to /admin/banners\n2. Create new banners\n3. Edit or delete existing ones\n4. Set visibility and expiration dates',
      
      'bulletins': 'To manage bulletins:\n1. Go to /admin/bulletins\n2. Create new bulletins\n3. Edit or delete existing ones\n4. Set visibility and expiration dates',
      
      'analytics': 'View analytics at /admin/analytics to see:\n- Job posting statistics\n- Application metrics\n- User engagement data\n- System usage reports',
      
      'documents': 'For document management:\n1. W2 documents at /admin/w2-documents\n2. Onboarding documents at /admin/onboarding\n3. Employee documents at /admin/documents',
      
      'settings': 'Access settings at /admin/settings to:\n- View your admin information\n- Configure system preferences\n- Manage chatbot settings',
      
      'default': "I'm not sure about that. You can ask me about:\n- Jobs and applications\n- Employer management\n- Banners and bulletins\n- Analytics\n- Documents\n- Settings\n\nOr type \"help\" for a full list of features."
    };

    // Check for keywords in the message
    for (const [key, response] of Object.entries(responses)) {
      if (message.includes(key)) {
        return response;
      }
    }

    return responses.default;
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!currentUser || currentUser.role !== 'admin' || !isAdminChatbotEnabled) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {!isOpen ? (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-orange-500 text-white p-3 rounded-full shadow-lg hover:bg-orange-600 transition-colors"
        >
          <MessageSquare size={24} />
        </button>
      ) : (
        <div className="bg-white rounded-lg shadow-xl w-96 h-[600px] flex flex-col">
          <div className="bg-orange-500 text-white p-4 rounded-t-lg flex justify-between items-center">
            <h3 className="font-semibold">Admin Assistant</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:text-gray-200"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${
                  message.type === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message.type === 'user'
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  <p className="whitespace-pre-line">{message.content}</p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="p-4 border-t">
            <div className="flex space-x-2">
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 border rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                rows={1}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim()}
                className="bg-orange-500 text-white p-2 rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={20} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminChatBot; 